import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/errors";
import { useDeleteProviderCredential, useProviderCredentials } from "../../api/queries";
import type { ProviderCredentialSummary } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useToast } from "../../toast/ToastContext";
import { AddProviderCredentialModal } from "./AddProviderCredentialModal";
import { DeleteProviderCredentialDialog } from "./DeleteProviderCredentialDialog";
import "./provider-credentials.css";

export const PROVIDER_LABEL: Record<ProviderCredentialSummary["kind"], string> = {
  hetzner: "Hetzner",
  digitalocean: "DigitalOcean",
  vultr: "Vultr",
  linode: "Linode (Akamai)",
};

const WRITE_ONLY_NOTE =
  "Provider tokens are write-only — sealed on write and never shown again. Rotate or add a new credential to replace one.";

export function ProviderCredentialsPage() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useProviderCredentials({
    cursor: paginator.cursor,
  });
  const deleteMutation = useDeleteProviderCredential();

  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProviderCredentialSummary | null>(null);

  // Mirrors the server's authz matrix exactly: provider_credential:create/
  // update/delete are admin-only (server/internal/authz/model.go) — holding
  // one of these tokens is the ability to spend the tenant's whole cloud
  // budget. This is UX only (S-8) — the server enforces the real decision
  // on every request regardless of what this check says.
  const isAdmin = session?.role === "admin";

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id, label } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Removed "${label}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't remove "${label}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const addButton = (
    <Button variant="primary" onClick={() => setAddOpen(true)}>
      Add credential
    </Button>
  );

  const columns: DataTableColumn<ProviderCredentialSummary>[] = [
    { key: "label", header: "Label", cell: (c) => c.label, className: "data-table__primary" },
    { key: "kind", header: "Provider", cell: (c) => PROVIDER_LABEL[c.kind] },
    { key: "createdBy", header: "Added by", cell: (c) => c.createdBy || "—", className: "data-table__secondary" },
    {
      key: "createdAt",
      header: "Added",
      cell: (c) => new Date(c.createdAt).toLocaleString(),
      className: "data-table__secondary",
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (c: ProviderCredentialSummary) => (
              <Button variant="danger" onClick={() => setDeleteTarget(c)}>
                Remove
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Provider credentials"
        description="Cloud provider tokens Comebye uses to provision compute on your behalf."
        actions={isAdmin ? addButton : undefined}
      />

      <p className="provider-credentials-note">{WRITE_ONLY_NOTE}</p>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading provider credentials…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load provider credentials. Check your connection and try again."
        }
        renderError={(err) =>
          err instanceof ApiError && err.code === "not_implemented" ? (
            <EmptyState
              title="Cloud provisioning isn't turned on yet"
              description="Ask whoever runs this Comebye deployment to enable it. Nothing to configure here until then."
            />
          ) : undefined
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No provider credentials yet"
            description="Add a cloud provider credential to start provisioning instances."
            action={isAdmin ? addButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(c) => c.id} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="credentials"
            />
          </>
        )}
      </QueryStates>

      {isAdmin && <AddProviderCredentialModal open={addOpen} onClose={() => setAddOpen(false)} />}

      {isAdmin && (
        <DeleteProviderCredentialDialog
          credential={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
